/* generated using openapi-typescript-codegen -- do no edit */
/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { CancelablePromise } from '../core/CancelablePromise'
import type { BaseHttpRequest } from '../core/BaseHttpRequest'

export class MetricsService {
    constructor(public readonly httpRequest: BaseHttpRequest) {}

    /**
     * Metric names available to fetch from `/metrics/data`, filtered to what the current user is permitted to view. No snapshot values here, just names. The list mixes two kinds of name with no marker distinguishing them - see `/metrics/data` for how to tell them apart and how each behaves once requested.
     * @returns any OK
     * @throws ApiError
     */
    public getMetrics(): CancelablePromise<{
        data: Array<string>
    }> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/metrics',
            errors: {
                401: `User is unauthorized.`,
                403: `Access Denied`
            }
        })
    }

    /**
     * Snapshot history for one or more metrics in a single query, keyed by
     * the requested metric name. A ratio metric (MetricSnapshotPolicy::RATIO_DEFINITIONS)
     * resolves to a `{created_at, value}` series instead of `{dimension,
     * created_at, value}` - it has no dimension of its own, and only
     * carries a value at instants where both sides of the ratio have data.
     * Snapshot history for one or more metrics in a single request, keyed by the requested metric name.
     *
     * **Two kinds of metric, two response shapes.** Every name from `/metrics` is one of:
     * - **A recorded metric** (e.g. `feature_adoption`, `groups_adoption`, `active_organizations`) - written by the snapshot job, on whatever cadence its collector is scheduled at (nightly for an adoption metric, every few minutes for a health-indicator one). Resolves to a `{dimension, created_at, value}` array; `value` is an integer count, `dimension` splits the metric into sub-series (e.g. a feature id) and is `""` for a metric with no sub-dimension. Pass `dimension` to restrict the series to just one of them - omit it to get every dimension the metric has, mixed into one array.
     * - **A ratio metric** (currently `groups_adoption_rate`, `groups_chat_adoption_rate` - see `Breeze\Helm\Policies\MetricSnapshotPolicy::RATIO_DEFINITIONS` for the full, authoritative list) - computed on the fly from two recorded metrics, never stored itself. Resolves to a `{created_at, value}` array with **no `dimension` key** - a ratio has none of its own. `value` is a **fraction between 0 and 1** (e.g. `0.42`), not a percentage - multiply by 100 to display one.
     *
     * There's no field in the response marking which kind a metric is - the caller already knows, because it's the name it asked for.
     *
     * **A ratio's series can be shorter than a plain date range would suggest.** A ratio only has a value at an instant where *both* its numerator and denominator have a recorded snapshot with that exact `created_at` - an instant missing either side is left out of the array entirely rather than emitted with a null or zero. Don't assume one entry per day in range; index by `created_at` instead of by position.
     *
     * Example response for `?metrics[]=feature_adoption&metrics[]=groups_adoption_rate`:
     * ```json
     * {
     * "data": {
     * "feature_adoption": [
     * {"dimension": "12:5:org_disabled", "created_at": "2026-08-19T04:00:00.000000Z", "value": 3},
     * {"dimension": "12:5:org_disabled", "created_at": "2026-08-20T04:00:00.000000Z", "value": 5}
     * ],
     * "groups_adoption_rate": [
     * {"created_at": "2026-08-20T04:00:00.000000Z", "value": 0.42}
     * ]
     * }
     * }
     * ```
     * (Note `2026-08-19T04:00:00.000000Z` has no `groups_adoption_rate` entry above - one side of that ratio wasn't recorded at that instant.)
     *
     * Every metric in one request shares the same `start_date`/`end_date` window and costs a single query, regardless of how many are requested or whether any are ratios - request everything a chart needs in one call rather than one request per metric.
     *
     * Permissions are checked per requested metric, before the query runs - a request naming even one metric the caller can't read is rejected outright, none of it partially returned. A ratio's permission follows its numerator's recorded metric (see `MetricSnapshotPolicy`), not the ratio name itself.
     * @returns any OK
     * @throws ApiError
     */
    public getMetricsData({
        metrics,
        startDate,
        endDate,
        dimension,
        intervalMinutes
    }: {
        /**
         * The metric names to fetch - recorded or ratio, mixed freely in the same request. Get valid names from `/metrics`.
         */
        metrics: Array<string>
        /**
         * Filter by start date. Defaults to 90 days ago if omitted - this endpoint never returns unbounded history, so pass this explicitly for a longer window.
         */
        startDate?: string
        /**
         * Filter by end date. Omit for "through today" - there is no default upper bound.
         */
        endDate?: string
        /**
         * Restricts each requested recorded metric to this one dimension's series, instead of returning every dimension it has. Ignored by a ratio metric, whose numerator and denominator dimensions are already fixed by MetricSnapshotPolicy::RATIO_DEFINITIONS.
         */
        dimension?: string
        /**
         * Average each series into buckets of this many minutes, keyed by the bucket's start, instead of returning every recorded point. Omit for every recorded point.
         */
        intervalMinutes?: 5 | 15 | 30 | 60 | 180 | 360 | 720 | 1440
    }): CancelablePromise<{
        data: Record<
            string,
            Array<{
                /**
                 * Absent for a ratio metric - a ratio has no dimension of its own.
                 */
                dimension?: string
                created_at: string
                value: number
            }>
        >
    }> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/metrics/data',
            query: {
                'metrics[]': metrics,
                'start_date': startDate,
                'end_date': endDate,
                'dimension': dimension,
                'interval_minutes': intervalMinutes
            },
            errors: {
                401: `User is unauthorized.`,
                403: `Access Denied`
            }
        })
    }
}
